import React, { useState, useEffect } from "react";
import "../styles/ManageMember.css";
import SideBar from "../components/SideBar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useLocation, useNavigate } from "react-router-dom";
import { accountAdminService } from "../services/accountAdminService";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
});

function ManageAccountAdmin() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [filteredAdminsCount, setFilteredAdminsCount] = useState(0);
  const [allAdmins, setAllAdmins] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllAdmins();
  }, []);

  useEffect(() => {
    if (location.state && location.state.message) {
      setSnackMessage(location.state.message);
      setSnackOpen(true);
    }
  }, [location.state]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, status]);

  useEffect(() => {
    const fetchData = async () => {
      if (search || status !== "all") {
        setIsFiltering(true);
        await fetchFilteredAdmins();
      } else {
        setIsFiltering(false);
      }
    };
    fetchData();
  }, [search, status, currentPage]);

  useEffect(() => {
    if (!isFiltering && allAdmins.length > 0) {
      const formattedAdmins = formatAdmins(allAdmins);
      const filtered = applyFilters(formattedAdmins);
      setFilteredAdminsCount(filtered.length);
      
      const itemsPerPage = 10;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);
      setAdmins(paginated);
    }
  }, [allAdmins, currentPage, isFiltering, search, status]);

  const fetchAllAdmins = async () => {
    try {
      setLoading(true);
      const response = await accountAdminService.getAllAccountAdmins({
        page: 1,
        per_page: 1000,
      });

      if (response.users && Array.isArray(response.users)) {
        setAllAdmins(response.users);
        setTotalAdmins(response.pagination?.total || response.users.length);
      }
    } catch (error) {
      console.error("Error fetching all account admins:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredAdmins = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 10,
        ...(search && { search }),
        ...(status !== "all" && { status: status.toLowerCase() }),
      };

      const response = await accountAdminService.getAllAccountAdmins(params);

      if (response.users && Array.isArray(response.users)) {
        setAdmins(formatAdmins(response.users));
        setFilteredAdminsCount(response.pagination?.total || response.users.length);
      } else {
        setAdmins([]);
        setFilteredAdminsCount(0);
      }
    } catch (error) {
      console.error("Error fetching filtered account admins:", error);
      setSnackMessage("Failed to load account admins");
      setSnackOpen(true);
      setAdmins([]);
      setFilteredAdminsCount(0);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (formattedAdmins) => {
    return formattedAdmins.filter((admin) => {
      const searchTerm = search.toLowerCase();

      const searchMatch =
        admin.id.toString().includes(searchTerm) ||
        admin.fullName.toLowerCase().includes(searchTerm) ||
        admin.email.toLowerCase().includes(searchTerm);

      const statusMatch =
        status === "all" ||
        admin.status.toLowerCase() === status.toLowerCase();

      return searchMatch && statusMatch;
    });
  };

  const handleSnackClose = () => setSnackOpen(false);

  const goToCreateAccount = () => {
    navigate("/create-account-admin");
  };

  const formatAdmins = (apiAdmins) => {
    return apiAdmins.map((admin) => ({
      id: admin.id,
      userId: admin.user_id,
      fullName: `${admin.first_name} ${admin.last_name}`,
      email: admin.email,
      phone: admin.phone_number,
      address: {
        city: admin.city || "N/A",
      },
      status: admin.current_status
        ? admin.current_status.charAt(0).toUpperCase() +
          admin.current_status.slice(1)
        : "Awaiting",
      createdAt: new Date(admin.created_at).toLocaleDateString(),
    }));
  };

  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredAdminsCount / itemsPerPage);
  const currentAdmins = isFiltering 
    ? admins 
    : applyFilters(formatAdmins(allAdmins)).slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">Manage Account Admins</h2>
            <button className="action-btn" onClick={goToCreateAccount}>
              + Add New Account Admin
            </button>
          </div>

          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Admins:</span>
              <span className="stat-value">{totalAdmins}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Admins:</span>
              <span className="stat-value">{filteredAdminsCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">{currentAdmins.length}</span>
            </div>
          </div>

          <div className="filters">
            <div className="input-group">
              <label>Filter by status:</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="awaiting">Awaiting</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="input-group" style={{ flexGrow: 1 }}>
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by ID, name, or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
               maxLength={20} />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "40px" }}>ID</th>
                <th style={{ width: "250px" }}>Full Name</th>
                <th style={{ width: "250px" }}>Email</th>
                <th style={{ width: "150px" }}>Phone</th>
                <th style={{ width: "150px" }}>City</th>
                <th style={{ width: "120px" }}>Joined</th>
                <th style={{ width: "120px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Account Admins...</div>
                  </td>
                </tr>
              ) : currentAdmins.length > 0 ? (
                currentAdmins.map((admin, i) => (
                  <tr key={admin.id || i}>
                    <td>{admin.id}</td>
                    <td>{admin.fullName}</td>
                    <td>{admin.email}</td>
                    <td>{admin.phone}</td>
                    <td>{admin.address.city}</td>
                    <td>{admin.createdAt}</td>
                    <td>
                      <span className={`status ${admin.status.toLowerCase()}`}>
                        {admin.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    {filteredAdminsCount === 0 && totalAdmins > 0
                      ? "No account admins match your filters"
                      : "No account admins found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!loading && filteredAdminsCount > 0 && totalPages > 1 && (
            <div className="pagination">
              <button
                className="page-btn"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page-btn ${
                    currentPage === i + 1 ? "active" : ""
                  }`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="page-btn"
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          icon={
            <span
              style={{
                fontWeight: "bold",
                fontSize: "18px",
                color: "rgba(51, 178, 51, 1",
              }}
            >
              ✔
            </span>
          }
          sx={{
            backgroundColor: "transparent",
            backdropFilter: "blur(8px)",
            color: "#000",
            border: "2px solid rgba(51, 178, 51, 1)",
            borderRadius: "10px",
            boxShadow: "none",
            fontSize: "15px",
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            height: "25px",
            width: "200px",
          }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ManageAccountAdmin;
